import styled from 'styled-components'
import { InvoicePaymentType } from '../../api/dto/invoice-delivery.dto'
import { ActionButtonsRow } from './BulkInvoiceUpload.styles'
import { sureco } from '../shared/finance.styles'

/*
 * Styles specific to the delivery-and-payment table (EMS-10148). The shared
 * delivery styles live in BulkInvoiceUpload.styles / InvoiceDeliveryTable.styles;
 * only the payment-column pieces belong here.
 */

/* Matches DrawerPaymentPill in InvoiceDeliveryDrawer.styles, so the same
   client reads the same way in the drawer and in the table. */
export const PaymentMethodPill = styled.span<{ $type: InvoicePaymentType }>`
  display: inline-block;
  padding: 1px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  background: ${({ $type }) =>
    $type === InvoicePaymentType.WIRE ? '#eff6ff' : '#f5f3ff'};
  color: ${({ $type }) =>
    $type === InvoicePaymentType.WIRE ? sureco.tealDark : '#6d28d9'};
`

/*
 * Tone rather than status: the backend owns the draw vocabulary, so the badge
 * is told how a status should read, not which status it is.
 */
export type DrawStatusTone = 'neutral' | 'progress' | 'success' | 'danger'

const DRAW_TONE_COLORS: Record<
  DrawStatusTone,
  { bg: string; text: string; dot: string }
> = {
  neutral: { bg: sureco.offWhite, text: sureco.fg2, dot: sureco.placeholder },
  progress: { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' },
  success: { bg: '#dcfce7', text: '#15803d', dot: '#22c55e' },
  danger: { bg: '#fee2e2', text: '#dc2626', dot: '#ef4444' },
}

export const DrawStatusBadge = styled.span<{ $tone: DrawStatusTone }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
  background: ${({ $tone }) => DRAW_TONE_COLORS[$tone].bg};
  color: ${({ $tone }) => DRAW_TONE_COLORS[$tone].text};

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ $tone }) => DRAW_TONE_COLORS[$tone].dot};
  }
`

/* Violet rather than the send button's blue: drawing money is a different kind
   of action from sending an email, and should not be reachable by muscle memory. */
export const DrawButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  line-height: 1.5;
  transition:
    background 0.15s,
    border-color 0.15s;
  border: 1px solid #c4b5fd;
  background: #f5f3ff;
  color: #6d28d9;

  &:hover:not(:disabled) {
    border-color: #a78bfa;
    background: #ede9fe;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const AmountCell = styled.span<{ $mismatched?: boolean }>`
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: ${({ $mismatched }) => ($mismatched ? '#f59e0b' : sureco.fg1)};
`

/*
 * Right-aligned variant of the shared ActionButtonsRow. Actions is the last
 * column, so pinning the controls to the right edge keeps them in one vertical
 * line no matter how many of them a row happens to show — Draw and Send both
 * come and go per row. The shared row stays left-aligned for the plain
 * delivery table.
 */
export const ActionButtonsRowRight = styled(ActionButtonsRow)`
  justify-content: flex-end;
`
